import { useState, useCallback } from 'react';
import { graphQlClient } from '@config/graphqlClient';
import IncidentsGraphQL from '@graphql/incident.queries';
import { Incident, IncidentStatus } from '@models/incident.model';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { SnackType } from '@models/common.model';
import { useTranslation } from '@hooks/useTranslation';

export const useIncident = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const fetchIncidentsByOrganization = useCallback(
    async (organizationId: string) => {
      setLoading(true);
      try {
        const data: any = await graphQlClient.request(
          IncidentsGraphQL.queries.getIncidentsByOrganization,
          { organizationId },
        );
        setIncidents(data.getIncidentsByOrganization || []);
        return data.getIncidentsByOrganization || [];
      } catch (error: any) {
        dispatch(
          appendActionMessage({
            message:
              error?.response?.errors[0]?.message ||
              localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
        return [];
      } finally {
        setLoading(false);
      }
    },
    [dispatch, localeCommon],
  );

  const fetchPendingIncidents = useCallback(
    async (organizationId: string) => {
      setLoading(true);
      try {
        const data: any = await graphQlClient.request(
          IncidentsGraphQL.queries.getPendingIncidents,
          { organizationId },
        );
        setIncidents(data.getPendingIncidents || []);
        return data.getPendingIncidents || [];
      } catch (error: any) {
        dispatch(
          appendActionMessage({
            message:
              error?.response?.errors[0]?.message ||
              localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
        return [];
      } finally {
        setLoading(false);
      }
    },
    [dispatch, localeCommon],
  );

  const fetchIncidentsByStatus = useCallback(
    async (organizationId: string, status: IncidentStatus) => {
      setLoading(true);
      try {
        const data: any = await graphQlClient.request(
          IncidentsGraphQL.queries.getIncidentsByStatus,
          { organizationId, status },
        );
        setIncidents(data.getIncidentsByStatus || []);
        return data.getIncidentsByStatus || [];
      } catch (error: any) {
        dispatch(
          appendActionMessage({
            message:
              error?.response?.errors[0]?.message ||
              localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
        return [];
      } finally {
        setLoading(false);
      }
    },
    [dispatch, localeCommon],
  );

  const fetchIncidentsByTask = useCallback(
    async (taskId: string) => {
      setLoading(true);
      try {
        const data: any = await graphQlClient.request(
          IncidentsGraphQL.queries.getIncidentsByTask,
          { taskId },
        );
        const incidents = data.getIncidentsByTask || [];
        setIncidents(incidents);
        return incidents;
      } catch (error: any) {
        dispatch(
          appendActionMessage({
            message:
              error?.response?.errors[0]?.message ||
              localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
        return [];
      } finally {
        setLoading(false);
      }
    },
    [dispatch, localeCommon],
  );

  const acknowledgeIncident = useCallback(
    async (incidentId: string) => {
      setLoading(true);
      try {
        const data: any = await graphQlClient.request(
          IncidentsGraphQL.mutations.acknowledgeIncident,
          { incidentId },
        );

        // Update the incident in the local state
        setIncidents((prev) =>
          prev.map((incident) =>
            incident.incidentId === incidentId
              ? { ...incident, ...data.acknowledgeIncident }
              : incident,
          ),
        );

        dispatch(
          appendActionMessage({
            message: 'Incident acknowledged successfully',
            type: SnackType.SUCCESS,
          }),
        );

        return data.acknowledgeIncident;
      } catch (error: any) {
        dispatch(
          appendActionMessage({
            message:
              error?.response?.errors[0]?.message ||
              localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, localeCommon],
  );

  return {
    incidents,
    loading,
    fetchIncidentsByOrganization,
    fetchPendingIncidents,
    fetchIncidentsByStatus,
    fetchIncidentsByTask,
    acknowledgeIncident,
  };
};
